import { type ApiTag } from './tags';

export type Params = Record<string, string | number | boolean | undefined | null>;

interface CacheTagOptions {
  baseTag: ApiTag;
  params?: Params;
  includeBaseTag?: boolean;
}

/**
 * Creates standardized cache tags for API endpoints
 * Allows for granular cache invalidation based on parameters
 *
 * @example
 * createCacheTags({
 *   baseTag: API_TAGS.CONSIGNMENTS,
 *   params: {
 *     projectId: 123,
 *     siteId: 456,
 *     page: 1
 *   }
 * })
 * // Returns: [
 * //   'Consignments',
 * //   { type: 'Consignments', id: 'projectId-123-siteId-456-page-1' }
 * // ]
 *
 * createCacheTags({
 *   params: {
 *     projectId: 123,
 *     siteId: 456
 *   }
 * })
 * // Returns: [
 * //   { type: 'Consignments', id: 'projectId-123-siteId-456' }
 * // ]
 */
export function createCacheTags({ baseTag, params, includeBaseTag = true }: CacheTagOptions) {
  const tags: Array<{ type: ApiTag; id: string }> = [];

  if (baseTag && includeBaseTag) {
    tags.push({ type: baseTag, id: baseTag });
  }

  if (params) {
    const segments: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        segments.push(`${key}-${value}`);
      }
    });

    // Add specific cache tag if we have segments
    if (segments.length > 0 && baseTag) {
      tags.push({ type: baseTag, id: segments.join('-') });
    }
  }

  return tags;
}
